import throttle from 'lodash.throttle';
import { useCallback, useEffect, useRef, useState } from 'react';

type RectProp = 'height' | 'width' | 'bottom' | 'top' | 'left' | 'right' | 'x' | 'y';

/**
 * Hook to get an element's size and position relative to the viewport
 * @param watchProps an array of `ClientRect` properties that want to watch for changes. Used to prevent unwanted re renders. Defaults to watching all properties. Pass `null` to only get the rect coordinates once
 */
export function useClientRect(
  watchProps: Array<RectProp> | null = [],
  dependencies?: ReadonlyArray<any>,
): [ClientRect | null, (instance: HTMLElement | null) => void] {
  const [rect, setRect] = useState<ClientRect | null>(null);
  const refContainer = useRef<HTMLElement | null>(null);

  // Use useCallback instead of useRef to ensure notifications
  // when the ref changes e.g. attaches to a different node
  const ref = useCallback(
    (node: HTMLElement | null) => {
      if (node !== null) {
        // Save the node as a ref so it can be referenced in effects
        refContainer.current = node;
        setRect(node.getBoundingClientRect());
      }
    },
    dependencies ? dependencies : [],
  );

  // Update the rect value on window resize or scroll
  useEffect(() => {
    if (watchProps === null) return;

    function handleRectChange() {
      setRect((prev) => {
        if (refContainer.current) {
          const next = refContainer.current.getBoundingClientRect();
          return shouldUpdate(prev, next, watchProps) ? next : prev;
        }
        return prev;
      });
    }

    // Throttle updates since getBoundingClientRect results in reflow
    const throttledChangeHandler = throttle(handleRectChange, 50);

    window.addEventListener('resize', throttledChangeHandler);
    window.addEventListener('scroll', throttledChangeHandler);

    return () => {
      window.removeEventListener('resize', throttledChangeHandler);
      window.removeEventListener('scroll', throttledChangeHandler);
    };
  }, []);

  return [rect, ref];
}

function shouldUpdate(
  prev: ClientRect | null,
  next: ClientRect,
  watchProps: Array<RectProp> | null,
) {
  if (watchProps === null) {
    return false;
  }

  // check if we have any properties to watch
  if (watchProps.length === 0) {
    return prev !== next;
  } else if (prev === null) {
    return next;
  } else {
    // Check watched properties. First value to show it has changed tells us we should update
    return watchProps.some((prop) => {
      return prev[prop] !== next[prop];
    });
  }
}
